import React from 'react';
import { Link } from 'react-router-dom';
import { DocPage, Note, Steps, Crumbs } from '../../components/documentation/web-integration';

const CONTENTS = [
    'Overview',
    'Payment created',
    'Customer pays',
    'Payment confirmed',
    'Settlement',
    'Refunds',
    'Disputes',
];

const STAGES = [
    <>
        <strong>Payment created</strong> — you share a payment link or invoice, or your website or app sends the order to
        WayaQuick.
    </>,
    <>
        <strong>Customer pays</strong> on the secure WayaQuick checkout with the method they prefer.
    </>,
    <>
        <strong>Payment confirmed</strong> — WayaQuick confirms the result with the bank and tells you straight away.
    </>,
    <>
        <strong>Settlement</strong> — the money is paid into your settlement account.
    </>,
    <>
        <strong>After the payment</strong> — you can refund the customer, or respond if they raise a dispute.
    </>,
];

const REFUND_STEPS = [
    <>Open the successful transaction in your dashboard.</>,
    <>
        Click <strong>Refund Customer</strong>. A payout opens with the customer's details and the amount already
        filled in.
    </>,
    <>Review and confirm the payout.</>,
];

function LifeCycle() {
    return (
        <DocPage
            seo={{
                title: 'Payment Life Cycle',
                description:
                    'How a WayaQuick payment flows: from creating the payment and checkout to confirmation, settlement, refunds and disputes.',
            }}
            eyebrow="Merchants · How payments work"
            title={
                <>
                    WayaQuick — <em>Payment Life Cycle</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Payment Life Cycle · Created, paid, confirmed, settled."
            prev={{ to: '/registration-guide', label: 'Registration Guide' }}
            next={{ to: '/payment', label: 'Payments' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Overview</h2>
                <p className="wq-lead">
                    Every WayaQuick payment goes through the same stages, whether it comes from a payment link, an
                    invoice, your website or your mobile app.
                </p>
                <Steps items={STAGES} />
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Payment created</h2>
                <p className="wq-p">A payment starts when you ask your customer to pay:</p>
                <ul className="wq-p wq-list">
                    <li>
                        <strong>Payment link or invoice</strong> — you create it in your dashboard and share it.
                    </li>
                    <li>
                        <strong>Website or app</strong> — when the customer checks out, your plugin, SDK or API call
                        sends the order to WayaQuick.
                    </li>
                </ul>
                <p className="wq-p">
                    WayaQuick creates a transaction with its own reference, linked to your order reference, and opens
                    the checkout. The amount is fixed at this point — the customer can't change it on the checkout
                    page.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">Customer pays</h2>
                <p className="wq-p">
                    The customer pays on the WayaQuick checkout by dynamic virtual account, bank transfer, card, USSD
                    or PayAttitude — whichever methods you have turned on. Their payment details are entered on
                    WayaQuick, not on your website, so you never handle card details yourself.
                </p>
                <Note label="Exact amounts only">
                    The customer must pay the exact amount. A payment for more or less is rejected automatically, and
                    if the money has already come through it is reversed to the customer. You don't need to do
                    anything.
                </Note>
                <p className="wq-p">
                    See{' '}
                    <Link to="/payment" className="wq-link">
                        Payments
                    </Link>{' '}
                    for how each method works.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[3]}>
                <h2 className="wq-h2">Payment confirmed</h2>
                <p className="wq-p">
                    WayaQuick confirms the payment with the customer's bank. There is no separate capture step — once
                    the bank confirms, the payment is complete. Then:
                </p>
                <ul className="wq-p wq-list">
                    <li>
                        The transaction in your dashboard changes to <strong>Successful</strong> or{' '}
                        <strong>Failed</strong>.
                    </li>
                    <li>The customer sees the result on the checkout page.</li>
                    <li>If you've integrated, WayaQuick sends a webhook with the result to your server.</li>
                </ul>
                <Note label="For developers">
                    Only deliver the order once the status is <code>SUCCESSFUL</code>. Confirm it from the webhook or
                    with a status check on your server — not from the customer's browser redirect. See the{' '}
                    <Link to="/standard-api-integration" className="wq-link">
                        Standard API Integration
                    </Link>{' '}
                    for details.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[4]}>
                <h2 className="wq-h2">Settlement</h2>
                <p className="wq-p">
                    Successful payments are settled to your default settlement account on a T+1 cycle — the day after
                    the transaction date (T). Once the money is paid into your account, the payment is marked as
                    settled.
                </p>
                <p className="wq-p">Add or change your settlement account at:</p>
                <Crumbs items={['Settings', 'WayaQuick Settings', 'Settlement Account']} />
                <Note label="Test mode">Payments made in Test mode use test money and are never settled.</Note>
            </section>

            <section className="wq-section" id={CONTENTS[5]}>
                <h2 className="wq-h2">Refunds</h2>
                <p className="wq-p">
                    You can return money to a customer for any successful payment — for example, if an order is
                    cancelled. Refunds are sent from your dashboard:
                </p>
                <Steps items={REFUND_STEPS} />
                <p className="wq-p">
                    Only successful payments can be refunded. Wrong-amount payments don't need a refund — they are
                    reversed to the customer automatically.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[6]}>
                <h2 className="wq-h2">Disputes</h2>
                <p className="wq-p">
                    If a customer complains about a payment, or their bank raises a chargeback, a dispute is opened
                    against the transaction. You'll find it under <strong>Disputes</strong> in your dashboard, where
                    you can review the details and accept or reject it.
                </p>
                <p className="wq-p">
                    Respond quickly and keep proof of delivery, such as receipts or delivery confirmations, to support
                    your answer.
                </p>
            </section>
        </DocPage>
    );
}

export default LifeCycle;
